import { makeAutoObservable, runInAction } from 'mobx';

export type CsvExportStatus = 'idle' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface CsvExportStartOptions {
    total: number;
    filename: string;
    title?: string;
    execute: (ctx: {
        signal: AbortSignal;
        onProgress: (loaded: number, total: number) => void;
    }) => Promise<void>;
}

/**
 * Global CSV export job state so progress survives route changes
 * (same idea as CoreBatchUploadStore + BatchUploadWidget).
 */
export class CoreCsvExportStore {
    public status: CsvExportStatus = 'idle';
    public loaded = 0;
    public total = 0;
    public filename = '';
    public title = 'Exporting messages';
    public errorMessage: string | undefined;

    private abortController: AbortController | null = null;
    private runPromise: Promise<void> | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get isActive(): boolean {
        return this.status === 'running';
    }

    public get isVisible(): boolean {
        return this.status !== 'idle';
    }

    public get progressLabel(): string {
        if (this.status === 'failed') {
            return this.errorMessage || 'Export failed';
        }
        if (this.status === 'cancelled') {
            return 'Export cancelled';
        }
        if (this.status === 'completed') {
            return this.total > 0
                ? `Export complete (${this.total.toLocaleString()} messages)`
                : 'Export complete';
        }
        if (this.total > 0) {
            return `Loading messages… (${this.loaded.toLocaleString()} of ${this.total.toLocaleString()})`;
        }
        return 'Loading messages…';
    }

    public async start(options: CsvExportStartOptions): Promise<void> {
        if (this.isActive) {
            throw new Error('An export is already in progress');
        }

        const controller = new AbortController();
        this.abortController = controller;

        runInAction(() => {
            this.status = 'running';
            this.loaded = 0;
            this.total = options.total;
            this.filename = options.filename;
            this.title = options.title || 'Exporting messages';
            this.errorMessage = undefined;
        });

        const run = async (): Promise<void> => {
            try {
                await options.execute({
                    signal: controller.signal,
                    onProgress: (loaded, total) => {
                        runInAction(() => {
                            this.loaded = loaded;
                            this.total = total;
                        });
                    },
                });

                if (controller.signal.aborted) {
                    runInAction(() => {
                        this.status = 'cancelled';
                    });
                    return;
                }

                runInAction(() => {
                    this.status = 'completed';
                    this.loaded = this.total || this.loaded;
                });
            } catch (err: any) {
                const isAbort =
                    err?.name === 'AbortError' ||
                    err?.message?.includes('abort') ||
                    err?.message?.includes('cancel') ||
                    controller.signal.aborted;

                runInAction(() => {
                    if (isAbort) {
                        this.status = 'cancelled';
                    } else {
                        this.status = 'failed';
                        this.errorMessage = err?.message || 'Failed to export all messages';
                    }
                });

                if (!isAbort) {
                    throw err;
                }
            } finally {
                this.abortController = null;
                this.runPromise = null;
            }
        };

        this.runPromise = run();
        return this.runPromise;
    }

    public cancel(): void {
        if (!this.isActive) {
            return;
        }
        this.abortController?.abort();
    }

    public dismiss(): void {
        if (this.isActive) {
            return;
        }
        runInAction(() => {
            this.status = 'idle';
            this.loaded = 0;
            this.total = 0;
            this.filename = '';
            this.errorMessage = undefined;
        });
    }
}

/** App-shell singleton — one export job at a time, survives messaging unmount. */
export const csvExportStore = new CoreCsvExportStore();
